import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, errMsg } from "@/lib/api";
import { t } from "@/i18n";

function Preview({ url, type, text }) {
  if (text !== undefined) return <pre className="whitespace-pre-wrap text-sm muted max-h-[60vh] overflow-auto">{text}</pre>;
  if (type.startsWith("image/")) return <img src={url} alt="source" className="max-h-[65vh] mx-auto rounded" />;
  if (type.startsWith("video/")) return <video src={url} controls className="w-full max-h-[65vh]" />;
  if (type.startsWith("audio/")) return <audio src={url} controls className="w-full" />;
  if (type === "application/pdf") return <iframe src={url} title="pdf" className="w-full h-[65vh] rounded" />;
  return <a href={url} download className="btn btn-ghost">{t("Download file")}</a>;
}

export function SourceViewer({ source, onClose }) {
  const api = useApi();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    if (!source) return;
    let url;
    setState({ loading: true });
    if (!source.has_file) {
      setState({ loading: false, text: source.extracted_text || t("(no content)") });
      return;
    }
    api.get(`/sources/${source.id}/file`, { responseType: "blob" })
      .then(async (r) => {
        const type = r.data.type || source.content_type || "";
        if (type.startsWith("text/")) return setState({ loading: false, text: await r.data.text() });
        url = URL.createObjectURL(r.data);
        setState({ loading: false, url, type });
      })
      .catch((e) => setState({ loading: false, error: errMsg(e) }));
    return () => url && URL.revokeObjectURL(url);
  }, [source, api]);

  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[var(--bg-surface)] text-[var(--text-primary)]" data-testid="source-viewer-dialog">
        <DialogHeader>
          <DialogTitle>{source?.filename}</DialogTitle>
        </DialogHeader>
        {state.loading && <p className="faint">{t("Loading original…")}</p>}
        {state.error && <p className="text-[var(--status-error)]">{state.error}</p>}
        {!state.loading && !state.error && <Preview {...state} />}
      </DialogContent>
    </Dialog>
  );
}
